import React from 'react';
import { XCircle, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
export function PainAndProduct() {
  return <>
      {/* SECCIÓN 2: EL GANCHO */}
      <section className="py-20 px-4 bg-white" data-id="element-95">
        <div className="max-w-3xl mx-auto text-center" data-id="element-96">
          <p className="text-xl md:text-2xl text-brand-text font-medium mb-8 leading-relaxed" data-id="element-97">
            Hay un momento que todas las maestras y todos los maestros uruguayos
            conocen.
          </p>
          <p className="text-lg md:text-xl text-brand-text mb-10 leading-relaxed" data-id="element-98">
            El mate está frío. El sol ya se fue. Y vos todavía estás frente a la
            computadora buscando una ficha de fracciones que esté alineada al
            EBI, que tenga rúbrica, que conecte con el contexto uruguayo y que
            no parezca sacada de un libro de hace diez años.
          </p>

          <div className="bg-brand-lightBlue border border-brand-sky p-8 rounded-2xl mb-12" data-id="element-99">
            <p className="text-xl font-bold text-brand-deep italic" data-id="element-100">
              "Mientras tanto · tu familia está en el otro cuarto. El lunes se
              acerca, y vos seguís buscando."
            </p>
          </div>

          <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-orange mb-6" data-id="element-101">
            Eso tiene que terminar.
          </h2>
          <p className="text-lg md:text-xl text-brand-text mb-8 leading-relaxed" data-id="element-102">
            No porque enseñar sea fácil. Sino porque la planificación ya fue
            hecha. Alguien ya hizo el trabajo. Ya alineó cada actividad al
            programa EBI de ANEP. Ya escribió las rúbricas con los 4 niveles. Ya
            organizó las 36 semanas del año.
          </p>
          <p className="text-lg md:text-xl text-brand-text mb-12 leading-relaxed" data-id="element-103">
            Y ese trabajo está disponible para vos · ahora mismo · en un PDF que
            descargás hoy y usás mañana.
            <br data-id="element-104" />
            <span className="font-bold text-brand-deep" data-id="element-105">
              Eso es Aula de Éxitos.
            </span>
          </p>

          <div className="bg-orange-50 border-l-4 border-brand-orange p-6 text-left rounded-r-2xl" data-id="element-106">
            <p className="text-lg font-bold text-brand-deep mb-2" data-id="element-107">
              Pero hay algo más.
            </p>
            <p className="text-brand-text font-medium" data-id="element-108">
              Porque si además de recuperar tus domingos querés convertir ese
              material en una fuente de ingresos genuina · si querés dar clases
              particulares sin preparar nada extra · si querés montar tu propio
              consultorio pedagógico o tu academia educativa en Uruguay ·
              entonces lo que tenés en tus manos es mucho más que un recurso.
              <br data-id="element-109" />
              <br data-id="element-110" />
              <span className="font-bold text-brand-orange text-[32px]" data-id="element-111">
                Es la base de un negocio.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* SECCIÓN 3: EL DIAGNÓSTICO */}
      <section className="py-20 px-4 bg-brand-base" data-id="element-112">
        <div className="max-w-4xl mx-auto" data-id="element-113">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-deep mb-12 text-center leading-tight" data-id="element-114">
            Si sos docente de primaria en Uruguay, esto te va a sonar familiar:
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-115">
            {/* Perfil 1 */}
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100" data-id="element-116">
              <div className="space-y-4" data-id="element-117">
                {['Domingos perdidos buscando material que nunca está alineado al EBI', 'Fichas armadas a las corridas sin secuencia ni estructura real', 'Evaluaciones sin rúbricas · sin los 4 niveles que pide el programa', 'Planificaciones para el director/a hechas la noche anterior', '8 áreas · 36 semanas · y todo sobre tus hombros', 'La sensación de que nunca alcanza el tiempo ni el sueldo'].map((pain, i) => <div key={i} className="flex items-start gap-3" data-id="element-118">
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-119" />
                    <p className="text-brand-text font-medium" data-id="element-120">{pain}</p>
                  </div>)}
              </div>
            </div>

            {/* Perfil 2 */}
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100" data-id="element-121">
              <p className="font-bold text-brand-deep mb-6" data-id="element-122">
                Y si además querés generar ingresos extra con tu formación
                docente:
              </p>
              <div className="space-y-4" data-id="element-123">
                {['Querés dar clases particulares pero la sola idea de preparar más material te agota', 'Sabés que hay demanda en tu barrio pero no tenés un sistema profesional para ofrecer', 'Tenés el título · la experiencia y las ganas · pero te falta la estructura', 'No sabés cuánto cobrar · cómo conseguir alumnos ni cómo mostrar resultados a los padres'].map((pain, i) => <div key={i} className="flex items-start gap-3" data-id="element-124">
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-125" />
                    <p className="text-brand-text font-medium" data-id="element-126">{pain}</p>
                  </div>)}
              </div>
            </div>
          </div>

          <div className="text-center max-w-2xl mx-auto" data-id="element-127">
            <p className="text-xl font-bold text-brand-deep mb-4" data-id="element-128">
              Esa combinación de agotamiento profesional y potencial financiero
              sin aprovechar es exactamente el problema que Aula de Éxitos
              resuelve.
            </p>
            <p className="text-brand-text font-medium" data-id="element-129">
              En sus dos dimensiones.
            </p>
          </div>
        </div>
      </section>

      {/* SECCIÓN 4: POR QUÉ LO ANTERIOR NO FUNCIONÓ */}
      <section className="py-20 px-4 bg-white" data-id="element-130">
        <div className="max-w-3xl mx-auto text-center" data-id="element-131">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-deep mb-8" data-id="element-132">
            No es que no hayas intentado resolver esto.
          </h2>
          <p className="text-lg text-brand-text mb-6 leading-relaxed" data-id="element-133">
            Lo intentaste con grupos de Facebook donde se comparten actividades.
            Las descargaste · pero no estaban alineadas al EBI. Eran de
            Argentina. O de España. O simplemente no tenían la estructura que el
            programa uruguayo requiere.
          </p>
          <p className="text-lg text-brand-text mb-6 leading-relaxed" data-id="element-134">
            Lo intentaste con materiales gratuitos de internet. Pasaste más
            tiempo filtrando lo que no servía que usando lo que sí servía.
          </p>
          <p className="text-lg text-brand-text mb-12 leading-relaxed" data-id="element-135">
            Lo intentaste armando tu propio material. Y funcionó · pero te costó
            horas que no tenías.
          </p>

          <div className="bg-brand-deep text-white p-8 md:p-10 rounded-3xl shadow-xl text-left" data-id="element-136">
            <p className="text-xl font-medium mb-6 text-brand-sky" data-id="element-137">
              El problema no es tu creatividad.
              <br data-id="element-138" />
              El problema no es tu compromiso.
            </p>
            <p className="text-xl md:text-2xl font-bold leading-relaxed" data-id="element-139">
              El problema es que nunca tuviste un sistema completo · listo ·
              alineado al EBI de Uruguay · con todo integrado: planificación ·
              actividades · evaluación · rúbricas · registro · y contexto
              uruguayo.
            </p>
            <p className="text-2xl font-display font-bold text-brand-orange mt-8" data-id="element-140">
              Hasta hoy.
            </p>
          </div>
        </div>
      </section>

      {/* SECCIÓN 5: PRODUCTO */}
      <section className="py-20 px-4 bg-brand-deep text-white text-center relative overflow-hidden" data-id="element-141">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-blue/40 rounded-full blur-[120px] pointer-events-none" data-id="element-142"></div>
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-143">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-5 py-2 rounded-full text-sm font-bold mb-8" data-id="element-144">
            <span data-id="element-145">🏆</span> Sistema que tod@s l@s maestr@s de primaria en
            Uruguay eligen!
          </div>

          <div className="text-brand-orange font-bold tracking-widest text-sm mb-4 uppercase" data-id="element-146">
            🌟 Tu Fénix Digital
          </div>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold mb-8 text-white drop-shadow-lg" data-id="element-147">
            AULA DE <span className="text-brand-orange" data-id="element-148">ÉXITOS</span>
          </h2>

          <p className="text-xl md:text-2xl font-medium mb-8 max-w-2xl mx-auto text-brand-sky leading-relaxed" data-id="element-149">
            El sistema de planificación anual más completo para docentes de
            primaria en Uruguay.
          </p>
          <p className="text-lg font-medium mb-12 text-gray-300" data-id="element-150">
            100% PDF. Acceso inmediato. Alineado al Programa EBI de ANEP Uruguay
            2026.
          </p>

          <p className="text-lg font-bold text-white mb-6 uppercase tracking-wider" data-id="element-151">
            Organizado en 4 tramos que cubren toda la educación primaria
            uruguaya:
          </p>
          <div className="flex flex-wrap justify-center gap-3" data-id="element-152">
            {['Inicial', '1° y 2°', '3° y 4°', '5° y 6°'].map((tramo, i) => <div key={i} className="bg-white/10 border border-white/20 px-6 py-3 rounded-xl font-bold text-lg" data-id="element-153">
                {tramo}
              </div>)}
          </div>
        </div>
      </section>
    </>;
}